import React from "react";
import { Box } from "@mui/material";
import { CaseSheetSection } from "./CaseSheetModalComponents";

// ─── Toggle option sets ───────────────────────────────────────────────────────

const weightGainPatternOptions = ["Gradual", "Sudden"];
const activityLevelOptions = ["Sedentary", "Moderate", "Heavy"];

// ─── Tick-list configs (rendered as Yes/No rows) ──────────────────────────────

/** Comorbidity list shared by Past Medical History and Family History. */
const COMORBIDITIES = [
  ["Diabetes Mellitus", "diabetes"],
  ["Pre-diabetes", "prediabetes"],
  ["Hypertension", "hypertension"],
  ["Dyslipidaemia", "dyslipidaemia"],
  ["Coronary Artery Disease", "cad"],
  ["Stroke", "stroke"],
  ["Fatty Liver (NAFLD/MASLD)", "fatty_liver"],
  ["Chronic Kidney Disease", "ckd"],
  ["Osteoarthritis", "osteoarthritis"],
  ["Gout", "gout"],
  ["Asthma", "asthma"],
  ["Hypothyroidism", "hypothyroidism"],
  ["Cushing's Syndrome", "cushings"],
  ["PCOS", "pcos"],
  ["Obstructive Sleep Apnea", "osa"],
  ["GERD", "gerd"],
  ["Depression / Anxiety", "depression_anxiety"],
];

// Family history repeats the comorbidity list; "Pre-diabetes" is not asked for
// on the family side, and Bariatric Surgery is asked for only there.
const FAMILY_HISTORY_ITEMS = [
  ...COMORBIDITIES.filter(([, key]) => key !== "prediabetes"),
  ["Bariatric Surgery", "bariatric_surgery"],
];

const MEDICATION_ITEMS = [
  ["Steroid Use", "steroid"],
  ["Antipsychotics", "antipsychotics"],
  ["Antidepressants", "antidepressants"],
  ["Insulin", "insulin"],
  ["Sulfonylureas", "sulfonylureas"],
  ["Hormonal Therapy", "hormonal_therapy"],
  ["Weight-loss Medication", "weight_loss_medication"],
  ["Supplements", "supplements"],
];

const ObesityClinicTab = ({
  renderTextField,
  renderYesNoField,
  renderOptionToggleField,
}) => {
  const renderYesNoList = (items, prefix) =>
    items.map(([label, key]) => renderYesNoField(label, `${prefix}${key}`));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* ── 2. Chief complaint ── */}
      <CaseSheetSection title="CHIEF COMPLAINT" defaultExpanded>
        {renderTextField(
          "Reason for Consultation",
          "obesity_reason_for_consultation",
          "Reason for consultation"
        )}
        {renderTextField(
          "Recent Weight Change (last 6–12 months)",
          "obesity_recent_weight_change",
          "e.g. gained 8 kg in 6 months"
        )}
        {renderOptionToggleField(
          "Weight Gain Pattern",
          "obesity_weight_gain_pattern",
          weightGainPatternOptions
        )}
      </CaseSheetSection>

      {/* ── 3. History of present illness ── */}
      <CaseSheetSection title="HISTORY OF PRESENT ILLNESS" defaultExpanded={false}>
        {renderTextField("Duration", "obesity_hpi_duration", "Duration", false)}
        {renderTextField(
          "Details",
          "obesity_hpi_details",
          "History of present illness"
        )}
      </CaseSheetSection>

      {/* ── 4. Dietary assessment ── */}
      <CaseSheetSection
        title="DIETARY ASSESSMENT (24-HOUR RECALL)"
        defaultExpanded={false}
      >
        {renderTextField("Meal Timing", "obesity_meal_timing", "Meal timing")}
        {renderTextField("Portion Size", "obesity_portion_size", "Portion size")}
        {renderTextField(
          "Snacking Frequency",
          "obesity_snacking_frequency",
          "Snacking frequency"
        )}
        {renderYesNoField("Emotional Eating", "obesity_emotional_eating")}
        {renderYesNoField("Night Eating", "obesity_night_eating")}
        {renderYesNoField("Binge Eating", "obesity_binge_eating")}
        {renderTextField(
          "Sugary Beverages",
          "obesity_sugary_beverages",
          "Type & frequency"
        )}
        {renderTextField(
          "Alcohol Intake",
          "obesity_alcohol_intake",
          "Type & quantity"
        )}
        {renderTextField(
          "Fast-food Frequency",
          "obesity_fast_food_frequency",
          "Fast-food frequency"
        )}
        {renderTextField(
          "Fruit & Vegetable Intake",
          "obesity_fruit_vegetable_intake",
          "Servings per day"
        )}
        {renderTextField(
          "Water Intake",
          "obesity_water_intake",
          "Litres per day",
          false
        )}
      </CaseSheetSection>

      {/* ── 5. Physical activity ── */}
      <CaseSheetSection title="PHYSICAL ACTIVITY" defaultExpanded={false}>
        {renderOptionToggleField(
          "Activity Level",
          "obesity_activity_level",
          activityLevelOptions
        )}
        {renderTextField(
          "Exercise Type",
          "obesity_exercise_type",
          "Exercise type",
          false
        )}
        {renderTextField(
          "Exercise Frequency",
          "obesity_exercise_frequency",
          "e.g. 4 days/week",
          false
        )}
        {renderTextField(
          "Exercise Duration",
          "obesity_exercise_duration",
          "e.g. 30 minutes",
          false
        )}
        {renderTextField(
          "Daily Step Count",
          "obesity_daily_step_count",
          "Steps per day",
          false
        )}
        {renderTextField(
          "Screen Time",
          "obesity_screen_time",
          "Hours per day",
          false
        )}
        {renderTextField(
          "Sitting Hours / Day",
          "obesity_sitting_hours",
          "Hours per day",
          false
        )}
      </CaseSheetSection>

      {/* ── 6. Past medical history ── */}
      <CaseSheetSection title="PAST MEDICAL HISTORY" defaultExpanded={false}>
        {renderYesNoField("Diabetes Mellitus", "obesity_pmh_diabetes")}
        {renderTextField(
          "Diabetes Duration",
          "obesity_pmh_diabetes_duration",
          "e.g. 5 years",
          false
        )}
        {renderYesNoList(
          COMORBIDITIES.filter(([, key]) => key !== "diabetes"),
          "obesity_pmh_"
        )}
        {renderTextField("Others", "obesity_pmh_others", "Other conditions")}
      </CaseSheetSection>

      {/* ── 6b. Medication history ── */}
      <CaseSheetSection title="MEDICATION HISTORY" defaultExpanded={false}>
        {renderTextField(
          "Current Medications",
          "obesity_med_current",
          "Current medications"
        )}
        {renderYesNoList(MEDICATION_ITEMS, "obesity_med_")}
        {renderTextField("Others", "obesity_med_others", "Other medications")}
      </CaseSheetSection>

      {/* ── 7. Surgical history ── */}
      <CaseSheetSection title="SURGICAL HISTORY" defaultExpanded={false}>
        {renderTextField(
          "Surgical History",
          "obesity_surgical_history",
          "Past surgeries with dates"
        )}
      </CaseSheetSection>

      {/* ── 8. Family history ── */}
      <CaseSheetSection title="FAMILY HISTORY" defaultExpanded={false}>
        {renderYesNoList(FAMILY_HISTORY_ITEMS, "obesity_fh_")}
        {renderTextField("Any Others", "obesity_fh_others", "Other conditions")}
      </CaseSheetSection>

      {/* ── 9. Anthropometry ── */}
      <CaseSheetSection
        title="ANTHROPOMETRIC MEASUREMENTS"
        defaultExpanded={false}
      >
        {renderTextField("Body Height", "obesity_height", "cm", false)}
        {renderTextField("Weight", "obesity_weight", "kg", false)}
        {renderTextField("BMI", "obesity_bmi", "kg/m²", false)}
        {renderTextField(
          "Waist Circumference",
          "obesity_waist_circumference",
          "cm",
          false
        )}
        {renderTextField(
          "Hip Circumference",
          "obesity_hip_circumference",
          "cm",
          false
        )}
        {renderTextField(
          "Waist–Hip Ratio",
          "obesity_waist_hip_ratio",
          "Ratio",
          false
        )}
        {renderTextField(
          "Waist–Height Ratio",
          "obesity_waist_height_ratio",
          "Ratio",
          false
        )}
        {renderTextField(
          "Neck Circumference",
          "obesity_neck_circumference",
          "cm",
          false
        )}
        {renderTextField(
          "Body Composition — BIA",
          "obesity_body_composition_bia",
          "BIA findings"
        )}
        {renderTextField(
          "Body Composition — DEXA",
          "obesity_body_composition_dexa",
          "DEXA findings"
        )}
      </CaseSheetSection>

      {/* ── 10. Vitals ── */}
      <CaseSheetSection title="VITALS" defaultExpanded={false}>
        {renderTextField("BP", "obesity_bp", "e.g. 120/80 mmHg", false)}
        {renderTextField("Pulse", "obesity_pulse", "bpm", false)}
        {renderTextField("SpO₂", "obesity_spo2", "%", false)}
        {renderTextField("Temperature", "obesity_temperature", "°F / °C", false)}
        {renderTextField(
          "Respiratory Rate",
          "obesity_respiratory_rate",
          "breaths/min",
          false
        )}
      </CaseSheetSection>

      {/* ── 11. Systemic examination ── */}
      <CaseSheetSection title="SYSTEMIC EXAMINATION" defaultExpanded={false}>
        {renderTextField(
          "Skin",
          "obesity_exam_skin",
          "Acanthosis nigricans / skin tags / striae / intertrigo / hirsutism / xanthomas"
        )}
        {renderTextField("Thyroid", "obesity_exam_thyroid", "Goitre")}
        {renderTextField(
          "Cardiovascular",
          "obesity_exam_cardiovascular",
          "Heart sounds, murmurs, JVP, peripheral oedema"
        )}
        {renderTextField(
          "Abdomen",
          "obesity_exam_abdomen",
          "Hepatomegaly / abdominal wall hernias"
        )}
        {renderTextField(
          "Musculoskeletal",
          "obesity_exam_musculoskeletal",
          "Genu valgum / hip joint assessment / mobility"
        )}
        {renderTextField(
          "Respiratory",
          "obesity_exam_respiratory",
          "Wheeze / OHS / asthma"
        )}
        {renderTextField(
          "Reproductive",
          "obesity_exam_reproductive",
          "Menstrual irregularity, hyperandrogenism, PCOS symptoms, pregnancy history, menopause, infertility, gestational diabetes"
        )}
      </CaseSheetSection>

      {/* ── 12. Sleep assessment ── */}
      <CaseSheetSection title="SLEEP ASSESSMENT" defaultExpanded={false}>
        {renderTextField(
          "Sleep Duration",
          "obesity_sleep_duration",
          "Hours per night",
          false
        )}
        {renderYesNoField("Snoring", "obesity_snoring")}
        {renderYesNoField("Witnessed Apnea", "obesity_witnessed_apnea")}
        {renderYesNoField("Daytime Sleepiness", "obesity_daytime_sleepiness")}
        {renderYesNoField("Shift Work", "obesity_shift_work")}
        {renderTextField(
          "Sleep Quality",
          "obesity_sleep_quality",
          "Sleep quality"
        )}
      </CaseSheetSection>

      {/* ── 13. Psychological assessment ── */}
      <CaseSheetSection title="PSYCHOLOGICAL ASSESSMENT" defaultExpanded={false}>
        {renderTextField(
          "Psychological Assessment",
          "obesity_psychological_assessment",
          "Mood, stress, eating behaviour, body image"
        )}
      </CaseSheetSection>

      {/* ── 14. Substance use ── */}
      <CaseSheetSection title="SUBSTANCE USE" defaultExpanded={false}>
        {renderTextField(
          "Smoking",
          "obesity_substance_smoking",
          "Quantity & duration"
        )}
        {renderTextField(
          "Tobacco",
          "obesity_substance_tobacco",
          "Quantity & duration"
        )}
        {renderTextField(
          "Alcohol",
          "obesity_substance_alcohol",
          "Quantity & frequency"
        )}
        {renderTextField(
          "Recreational Drugs",
          "obesity_substance_recreational_drugs",
          "Details"
        )}
        {renderTextField("Any Others", "obesity_substance_others", "Details")}
      </CaseSheetSection>

      {/* ── 15. Weight-loss history ── */}
      <CaseSheetSection title="WEIGHT-LOSS HISTORY" defaultExpanded={false}>
        {renderTextField(
          "Previous Diet Plans",
          "obesity_previous_diet_plans",
          "Previous diet plans"
        )}
        {renderTextField(
          "Weight-loss Medications",
          "obesity_weight_loss_medications",
          "Medications tried"
        )}
        {renderTextField(
          "Maximum Weight-loss Achieved",
          "obesity_max_weight_loss",
          "e.g. 10 kg",
          false
        )}
        {renderTextField(
          "Weight Regain",
          "obesity_weight_regain",
          "e.g. 6 kg in 1 year",
          false
        )}
      </CaseSheetSection>

      {/* ── 17. Investigations ── */}
      <CaseSheetSection title="INVESTIGATIONS — TIER 1" defaultExpanded={false}>
        {renderTextField(
          "Fasting Blood Glucose",
          "obesity_inv_fbg",
          "mg/dL",
          false
        )}
        {renderTextField(
          "Post-Prandial Blood Glucose",
          "obesity_inv_ppbg",
          "mg/dL",
          false
        )}
        {renderTextField("HbA1c", "obesity_inv_hba1c", "%", false)}
        {renderTextField(
          "Fasting Lipid Profile",
          "obesity_inv_lipid_profile",
          "Total cholesterol, LDL, HDL, VLDL, triglycerides, non-HDL"
        )}
        {renderTextField(
          "Liver Function Test (LFT)",
          "obesity_inv_lft",
          "AST, ALT, ALP, GGT, bilirubin, total protein, albumin"
        )}
        {renderTextField(
          "Renal Function Test (RFT)",
          "obesity_inv_rft",
          "Urea, creatinine, eGFR, uric acid"
        )}
        {renderTextField("TFT — TSH", "obesity_inv_tsh", "TSH", false)}
        {renderTextField(
          "CBC with Peripheral Smear",
          "obesity_inv_cbc",
          "CBC findings"
        )}
        {renderTextField(
          "Serum Electrolytes",
          "obesity_inv_electrolytes",
          "Na, K, chloride, bicarbonate"
        )}
        {renderTextField(
          "Calcium, Phosphorus, ALP",
          "obesity_inv_calcium_phosphorus_alp",
          "Values"
        )}
        {renderTextField("Vitamin D", "obesity_inv_vitamin_d", "ng/mL", false)}
        {renderTextField("Vitamin B12", "obesity_inv_vitamin_b12", "pg/mL", false)}
        {renderTextField(
          "Ferritin / Iron / TIBC",
          "obesity_inv_iron_studies",
          "Values"
        )}
        {renderTextField(
          "Fasting Insulin, HOMA-IR",
          "obesity_inv_fasting_insulin_homa_ir",
          "Values"
        )}
        {renderTextField("hs-CRP", "obesity_inv_hscrp", "mg/L", false)}
      </CaseSheetSection>

      <CaseSheetSection title="INVESTIGATIONS — TIER 2" defaultExpanded={false}>
        {renderTextField("Serum Cortisol", "obesity_inv_cortisol", "Value", false)}
        {renderTextField(
          "LH:FSH Ratio, Testosterone, DHEAS, Prolactin",
          "obesity_inv_hormonal_panel",
          "Values"
        )}
        {renderTextField(
          "Anti-TPO / Anti-Tg Antibodies",
          "obesity_inv_thyroid_antibodies",
          "Values"
        )}
        {renderTextField(
          "Serum Uric Acid",
          "obesity_inv_uric_acid",
          "mg/dL",
          false
        )}
        {renderTextField("NT-proBNP", "obesity_inv_nt_probnp", "Value", false)}
        {renderTextField(
          "Homocysteine, Lp(a)",
          "obesity_inv_homocysteine_lpa",
          "Values"
        )}
        {renderTextField(
          "Fibrinogen, D-Dimer",
          "obesity_inv_fibrinogen_d_dimer",
          "Values"
        )}
        {renderTextField("APTT, PT-INR", "obesity_inv_aptt_pt_inr", "Values")}
        {renderTextField("ANA, Anti-dsDNA", "obesity_inv_ana_dsdna", "Values")}
        {renderTextField("Urine for ACR", "obesity_inv_urine_acr", "Value", false)}
        {renderTextField(
          "Urine Cortisol (24-hour)",
          "obesity_inv_urine_cortisol",
          "Value",
          false
        )}
      </CaseSheetSection>
    </Box>
  );
};

export default ObesityClinicTab;
